import { DataFetcher } from "components/DataFetcher";
import DataTable from "components/DataTable/DataTable";
import Pod from "components/Pod/Pod";
import { FilterMatchMode } from "primereact/api";
import { useCallback, useState } from "react";
import DateRangeInput from "../components/DateRangeInput";
import "./Timeline.scss";
import { getActivityClass, getActivityDescription, getActivityTitle } from "./activityUtilities";

// DataFetcher request contexts.
const RequestContext = Object.freeze({
	GetFirstActivity: "GetFirstActivity",
	GetTimeline: "GetTimeline"
});

const Timeline = () => {
	const [fetchData, setFetchData] = useState({ url: "/api/v3/Report/Timeline/FirstActivity/ByCompany", context: RequestContext.GetFirstActivity });
	const [filters, setFilters] = useState({
		global: { value: null, matchMode: FilterMatchMode.CONTAINS }
	});
	const [earliestDataDate, setEarliestDataDate] = useState("");
	const [timelineData, setTimelineData] = useState(null);
	
	// This function does not need to be added to dependency arrays, as it is guaranteed stable (a useCallback with no dependencies).
	const setFetchRequestData = useCallback(({ url, options = null, context = null }) => {
		setFetchData({ url, options, context });
	}, []);
	
	const handleFilter = useCallback((searchValue) => {
		const value = searchValue;
		let _filters = { ...filters };
		_filters["global"].value = value;
		setFilters(_filters);
	}, [filters]);

	/**
	 * @note Fetch request different success return data:
	 * GET /api/v3/Report/Timeline/FirstActivity/ByCompany - string
	 * GET /api/v3/Report/Timeline/ByCompany/{period} - List<Timeline>
	 */
	const handleData = useCallback((fetchedData, token, context) => {
		if (!fetchedData) {
			return;
		}
		if (context === RequestContext.GetFirstActivity) {
			setEarliestDataDate(fetchedData);
		}
		else {
			for (let item of fetchedData) {
				item.filter = getActivityTitle(item.activity) + getActivityDescription(item.activity, item.user, item.obj);
				item.filter = item.filter.replace(/<[^>]*>?/gm, "");
			}
			setTimelineData(fetchedData);
			handleFilter("");
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // handleFilter causes several "Maximum update depth exceeded" warnings in DevTools (despite it being a callback)...

	// { from, to }
	const handleRangeChange = useCallback((range) => {
		if (range && "from" in range && "to" in range) {
			const uriParam = range.from.getTime() + "," + range.to.getTime();
			setFetchRequestData({ url: "api/v3/Report/Timeline/ByCompany/" + uriParam, context: RequestContext.GetCourse });
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // setFetchRequestData

	const prettyPrintDate = (item) => {
		let date = new Date(item);
		let datePart = date.toLocaleDateString("en-GB", { "day": "numeric", "month": "numeric", "year": "2-digit" });
		let timePart = date.toLocaleTimeString("en-GB", { "hour": "numeric", "minute": "numeric" });
		return <><strong>{datePart}</strong> {timePart}</>;
	};

	const prettyPrintActivity = (item) => {
		const className = getActivityClass(item);
		const title = getActivityTitle(item);
		return <div className={`activity-pill ${className}`} data-test-class="timeline-activity-pill">{title}</div>;
	};

	const prettyPrintDetails = (item) => {
		return getActivityDescription(item.activity, item.user, item.obj);
	};

	const columns = [
		{
			field: "Date",
			headerName: "Date",
			class: "date-column",
			valueGetter: (data) => { return prettyPrintDate(data.timestamp); }
		},
		{
			field: "Activity",
			headerName: "Activity",
			class: "activity-column",
			valueGetter: (data) => { return prettyPrintActivity(data.activity); }
		},
		{
			field: "Details",
			headerName: "Details",
			valueGetter: (data) => { return prettyPrintDetails(data); }
		}
	];

	return (
		<div className="gutters">
			<DataFetcher fetchData={fetchData} setData={handleData} />
			<Pod title="Timeline" data-test-id="reports-timeline-pod">
				<div className="timeline-container">
					<DateRangeInput earliestDataDate={earliestDataDate} setRange={handleRangeChange} />
					<DataTable
						columns={columns}
						rows={timelineData}
						filters={filters}
						globalFilterFields={["filter"]}
						handleFilter={handleFilter}
						columnIdName="Id"
						useCardView={true}
						data-test-id="reports-timeline-table"
					/>
				</div>
			</Pod>
		</div>
	);
};

export default Timeline;